/**
 * FAQ Section Component
 * Displays frequently asked questions with expandable answers
 */

import { i18n } from '../utils/i18n'

export interface FAQItem {
  question: string
  answer: string
}

export interface FAQConfig {
  title: string
  subtitle: string
  items: FAQItem[]
}

export class FAQ {
  private config: FAQConfig
  public element: HTMLElement

  constructor(config: FAQConfig) {
    this.config = config
    this.element = this.render()
    this.setupLanguageListener()
  }

  /**
   * Render the FAQ section
   */
  render(): HTMLElement {
    const section = document.createElement('section')
    section.id = 'faq'
    section.className = 'faq-section'

    section.innerHTML = `
      <div class="faq-container">
        <div class="faq-header">
          <h2 class="faq-title" data-i18n="faq.title">${this.config.title}</h2>
          <p class="faq-subtitle" data-i18n="faq.subtitle">${this.config.subtitle}</p>
        </div>
        
        <div class="faq-items">
          ${this.config.items.map((item, index) => `
            <div class="faq-item" data-index="${index}">
              <button class="faq-question" aria-expanded="false">
                <span class="faq-question-text" data-i18n="faq.items.${index}.question">${item.question}</span>
                <span class="faq-icon">+</span>
              </button>
              <div class="faq-answer">
                <p data-i18n="faq.items.${index}.answer">${item.answer}</p>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `

    this.attachEventListeners(section)
    return section
  }

  /**
   * Attach event listeners to FAQ items
   */
  private attachEventListeners(section: HTMLElement): void {
    const questions = section.querySelectorAll('.faq-question')
    
    questions.forEach(question => {
      question.addEventListener('click', () => {
        const faqItem = question.closest('.faq-item')
        const answer = faqItem?.querySelector('.faq-answer')
        const icon = question.querySelector('.faq-icon')
        const isExpanded = question.getAttribute('aria-expanded') === 'true'

        if (faqItem && answer && icon) {
          if (isExpanded) {
            // Collapse
            question.setAttribute('aria-expanded', 'false')
            faqItem.classList.remove('active')
            icon.textContent = '+'
          } else {
            // Expand
            question.setAttribute('aria-expanded', 'true')
            faqItem.classList.add('active')
            icon.textContent = '−'
          }
        }
      })
    })
  }

  /**
   * Setup listener for language changes
   */
  private setupLanguageListener(): void {
    window.addEventListener('languageChanged', () => {
      // Save current expanded states
      const expandedStates: boolean[] = []
      const questions = this.element.querySelectorAll('.faq-question')
      questions.forEach(question => {
        expandedStates.push(question.getAttribute('aria-expanded') === 'true')
      })

      // Update content with new language
      const questionTexts = this.element.querySelectorAll('.faq-question-text')
      const answerTexts = this.element.querySelectorAll('.faq-answer p')

      questionTexts.forEach((el) => {
        const key = el.getAttribute('data-i18n')
        if (key) {
          el.textContent = i18n.t(key)
        }
      })

      answerTexts.forEach((el) => {
        const key = el.getAttribute('data-i18n')
        if (key) {
          el.textContent = i18n.t(key)
        }
      })

      // Restore expanded states
      questions.forEach((question, index) => {
        const faqItem = question.closest('.faq-item')
        const icon = question.querySelector('.faq-icon')
        
        if (expandedStates[index]) {
          question.setAttribute('aria-expanded', 'true')
          faqItem?.classList.add('active')
          if (icon) icon.textContent = '−'
        } else {
          question.setAttribute('aria-expanded', 'false')
          faqItem?.classList.remove('active')
          if (icon) icon.textContent = '+'
        }
      })
    })
  }
}
